import React, { memo, useEffect, useState } from 'react';
import { Point } from 'highcharts';
import Highcharts from 'highcharts/esm/highcharts.js';
import CustomEvents from 'highcharts-custom-events';
import { HighchartsReact } from 'highcharts-react-official';
import ReactDOMServer from 'react-dom/server';
import { FormatNumberForChart } from '@components-clean/core/charts/shared/number-utils';
import { CHART_HEIGHT_PIXELS, getCreditsStyle, getChartEvents } from '@components-clean/core/charts/shared/utils';
import Loading from '@components-clean/core/loading/loading';
import Tooltip from '@components-clean/core/tooltip';
import {
    ChartHorizontalLine,
    Column,
    HighchartsWithEvents,
    SeriesDataColumn,
} from '@interfaces-clean/panels.interface';
import { getThemeColours } from '@utils-clean/highchart-options/highchart-themes';
import { PanelTheme } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';

CustomEvents(Highcharts);

export interface ColumnChartProps {
    horizontalLines?: ChartHorizontalLine[];
    showLegend?: boolean;
    chartRef?: React.RefObject<HighchartsReact.RefObject | null>;
    panelData: Column;
    theme?: PanelTheme;
    showNumSubjects: boolean;
    showWaterMark?: boolean;
    title?: string;
}

const Chart = (props: ColumnChartProps) => {
    const {
        chartRef,
        showLegend,
        horizontalLines,
        panelData,
        theme = PanelTheme.NONE,
        showNumSubjects = false,
        showWaterMark = false,
        title,
    } = props;
    const [options, setOptions] = useState<HighchartsWithEvents | null>(null);
    const [open, setOpen] = useState<boolean>(false);
    const [targetElement, setTargetElement] = useState<EventTarget | null>(null);
    const { t } = useTranslation();

    const getItemWidth = (series: SeriesDataColumn[]) => {
        const longestName = series.reduce((a, b) => (a.name.length > b.name.length ? a : b)).name.length;
        const maxCharacterWidth = 11;
        const legendPointWith = 15;

        const minWidth = Math.max(longestName * maxCharacterWidth + legendPointWith, 100);

        return Math.min(minWidth, 200);
    };
    const { studyData, xTitle, yTitle, flagFieldForStudy } = panelData;

    const getSeriesData = () => {
        const seriesData: SeriesDataColumn[] = [
            {
                name: 'Series',
                values: studyData.map((x) => x.count),
            },
        ];
        return seriesData.reverse();
    };

    useEffect(() => {
        const categories = studyData.map((x) => x.category);
        const seriesData = getSeriesData();

        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setOptions({
            colors: getThemeColours(theme),
            credits: showWaterMark ? getCreditsStyle(`${t('cm:waterMarkMainPanelText')}`) : { enabled: false },
            chart: {
                type: 'column',
                zooming: {
                    type: 'xy',
                },
                height: CHART_HEIGHT_PIXELS,
                events: getChartEvents(showWaterMark, false, title),
            },
            exporting: {
                chartOptions: {
                    title: { text: title, style: { fontWeight: 'semi-bold' } },
                    credits: { enabled: false },
                },
                enabled: !chartRef,
                fallbackToExportServer: false,
                buttons: {
                    contextButton: {
                        enabled: false,
                    },
                },
            },
            legend: {
                enabled: showLegend,
                symbolHeight: 9,
                symbolWidth: 10,
                symbolRadius: 2,
                itemStyle: {
                    textOverflow: 'ellipsis',
                },
                maxHeight: 70,
                itemWidth: getItemWidth(seriesData),
            },
            xAxis: {
                categories,
                title: {
                    text: xTitle || null,
                },
                labels: {
                    style: {
                        textOverflow: 'ellipsis',
                    },
                    events: {
                        mouseover(e: MouseEvent) {
                            if (e.target) {
                                setTargetElement(e.target);
                                setOpen(true);
                            }
                        },
                        mouseout() {
                            setTargetElement(null);
                            setOpen(false);
                        },
                    },
                },
            },
            tooltip: {
                formatter() {
                    const point = this as Point;
                    return ReactDOMServer.renderToStaticMarkup(
                        <div>
                            <b>{point.category}</b>
                            {showNumSubjects && flagFieldForStudy && (
                                <>
                                    <br /># Subjects: {FormatNumberForChart(flagFieldForStudy)}
                                </>
                            )}
                            {point.y != null && (
                                <>
                                    <br />
                                    Value: {FormatNumberForChart(point.y)}
                                </>
                            )}
                        </div>,
                    );
                },
            },
            yAxis: {
                title: {
                    text: yTitle,
                },
                plotLines:
                    horizontalLines === undefined
                        ? undefined
                        : horizontalLines.map<Highcharts.YAxisPlotLinesOptions>((pl) => ({
                              color: pl.color,
                              dashStyle: pl.style,
                              label: { text: pl.name ?? undefined },
                              value: pl.y,
                              zIndex: 1,
                          })),
            },
            series:
                seriesData?.map((s) => ({
                    type: 'column',
                    name: s.name,
                    color: s.color,
                    data: s.values,
                })) ?? [],
        });
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [props]);

    if (options === null) return <Loading />;

    return (
        <Tooltip anchorElement="pointer" open={open} targetElement={targetElement} position="left">
            <HighchartsReact ref={chartRef} highcharts={Highcharts} constructorType="chart" options={options} />{' '}
        </Tooltip>
    );
};
export const ColumnChart = memo(Chart);
